import "./ArticleStickyBar.css";

import ListenIcon from "@assets/article/listen.svg";
import ListenHoverIcon from "@assets/article/listenHover.svg";
import ShareIcon from "@assets/article/shareIcon.svg";
import SummariseIcon from "@assets/article/summariseIcon.svg";
import TranslateIcon from "@assets/article/translateIcon.svg";
import AISummaryCTA from "@components/Modules/AISummary/AISummaryCTA";
import TranslationCTA from "@components/Modules/Translation/TranslationCTA";
import { ShareSocialBar } from "@routes/Article/Components/ShareSocialBar";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import cx from "classnames";
import { useRef } from "react";

import { useStickyBarLayout } from "./hooks/useStickyBarLayout";
import { useStickyBarVisibility } from "./hooks/useStickyBarVisibility";
import { useTtsWidget } from "./hooks/useTtsWidget";

export type ArticleStickyBarProps = {
  article: ProcessedArticleData;
  pageNumber: number;
  isStatic?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  onVisibilityChange?: (isVisible: boolean) => void;
  isActive?: boolean;
};

export default function ArticleStickyBar({
  article,
  pageNumber,
  isStatic = false,
  gsChannels,
  articleSlotTarget,
  onVisibilityChange,
  isActive = true,
}: ArticleStickyBarProps) {
  const barRef = useRef<HTMLDivElement>(null);

  const { setLayoutHeights } = useStickyBarLayout(barRef);

  const { isVisible, progress } = useStickyBarVisibility({
    articleId: article.id,
    isStatic,
    isActive,
    onVisibilityChange,
    onShow: setLayoutHeights,
  });
  const { ttsVisible, toggleTts, listenBtnRef, isPlayerReady } = useTtsWidget(
    article.id,
  );

  const containerClasses = cx("v_stickyArticleNow", {
    v_articleActionsBar: isStatic,
    v_showStickyBar: isVisible && !isStatic,
  });

  return (
    <>
      <div className={containerClasses} ref={barRef}>
        <div className="v_articleRow">
          <div className="v_articleColumn v_titleColumn">
            <div className="v_articleTitle">
              <h3>{article.title}</h3>
            </div>
          </div>

          {article.aiSummary && (
            <div className="v_articleColumn v_aiSummarySection">
              <AISummaryCTA
                article={article}
                gsChannels={gsChannels}
                articleSlotTarget={articleSlotTarget}
                pageNumber={pageNumber}
                renderButton={(onClick) => (
                  <button className="v_summariseBtn" onClick={onClick}>
                    <img
                      src={SummariseIcon}
                      alt="Summarise"
                      width={24}
                      height={24}
                    />
                    Summarise&nbsp;&nbsp;
                  </button>
                )}
              />
            </div>
          )}

          {article.translated && (
            <div className="v_articleColumn v_translateSection">
              <TranslationCTA
                article={article}
                gsChannels={gsChannels}
                articleSlotTarget={articleSlotTarget}
                pageNumber={pageNumber}
                renderButton={(onClick) => (
                  <button className="v_translateBtn" onClick={onClick}>
                    <img
                      src={TranslateIcon}
                      alt="Translate"
                      width={21}
                      height={21}
                      className="v_translateIcon"
                    />
                    Translate
                  </button>
                )}
              />
            </div>
          )}

          {!isStatic && isPlayerReady && (
            <div className="v_articleColumn v_listenSection">
              <div className="relative">
                <button
                  onClick={toggleTts}
                  aria-pressed={ttsVisible}
                  aria-label={
                    ttsVisible ? "hide tts widget" : "show tts widget"
                  }
                  className="v_listenBtn"
                  style={ttsVisible ? { color: "#2B568F" } : undefined}
                  ref={listenBtnRef}
                >
                  <img
                    src={ttsVisible ? ListenHoverIcon : ListenIcon}
                    alt="Listen"
                    width={24}
                    height={24}
                  />
                  Listen
                </button>
              </div>
            </div>
          )}

          <div className="v_articleColumn v_shareArticle v_shareRow">
            <ShareSocialBar
              dateCreated={article.datetime}
              pageUrl={article.urlPath || ""}
              socialIconList={[
                "facebook",
                "telegram",
                "plainWhatsapp",
                "newsPost",
              ]}
              title={article.title}
              renderButton={(onClick) => (
                <button onClick={onClick}>
                  <img src={ShareIcon} alt="Share" width={24} height={24} />
                  <span className="v_label">Share</span>
                </button>
              )}
            />
          </div>
        </div>

        {!isStatic && (
          <div className="v_pageMeter">
            <div className="v_pageProgress" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      {!isStatic && article.id && (
        <div className="tts-sticky-widget-container flex sticky top-[175px] pt-[3px] justify-center">
          <div
            id={`tts-sticky-widget-${article.id}`}
            className="w-full md:w-[500px] px-[10px]"
          ></div>
        </div>
      )}
    </>
  );
}
